import React, { useState, useEffect, useRef } from 'react';
import store from 'store2';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Typography,
  Avatar,
  Card,
  CardContent,
  CardMedia,
  InputAdornment,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import UserAvatar from '@mui/icons-material/AccountCircle';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import HttpIcon from '@mui/icons-material/Http';
import { v4 as generateUUID } from 'uuid';
import axiosInstance from '../../utils/axiosInstance';

const ChatBot = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [endpoint, setEndpoint] = useState(store.get('chatbotEndpoint') || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages]);

  useEffect(() => {
    store.set('chatbotEndpoint', endpoint);
  }, [endpoint]);

  const handleSend = async () => {
    if (input.trim() === '' || endpoint.trim() === '') return;

    const userMessage = { type: 'user', content: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);

    const userQuery = [
      {
        "type": "text/plain",
        "content": input,
        "ref_id": generateUUID(),
        "metadata": {}
      }
    ];

    try {
      const response = await axiosInstance.post(`${endpoint}/api/query`, userQuery, { headers: { 'Content-Type': 'application/json' } });
      // eslint-disable-next-line
      const data = response.data;

      const botMessage = { type: 'bot', content: '', mediaType: null, mediaUrl: null };

      data.response.forEach(item => {
        if (item.type === 'text/plain') {
          botMessage.content += `${item.content}\n`;
        } else if (item.type === 'image/jpeg') {
          botMessage.mediaType = 'image';
          botMessage.mediaUrl = `data:image/jpeg;base64,${item.content}`;
        } else if (item.type === 'video/mp4' || item.type === 'video/mp2t') {
          botMessage.mediaType = 'video';
          botMessage.mediaUrl = `data:${item.type};base64,${item.content}`;
        }
      });

      botMessage.content = botMessage.content.trim();
      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error('Error fetching response:', error);
      setError('An error occurred while fetching the response. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (endpoint.trim() === '') return;

    setIsLoading(true);
    setError(null);

    try {
      await axiosInstance.delete(`${endpoint}/api/memory`, {}, { headers: { 'Content-Type': 'application/json' } });
      setMessages([]);
      setInput('');
      const botMessage = { type: 'bot', content: 'Memory deleted successfully', mediaType: 'text', mediaUrl: null };
      setMessages([botMessage]);
    } catch (error) {
      console.error('Error deleting memory:', error);
      setError('An error occurred while deleting the memory. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderMessage = (message, index) => {
    const isBot = message.type === 'bot';
    return (
      <Box
        key={index}
        sx={{
          display: 'flex',
          justifyContent: isBot ? 'flex-start' : 'flex-end',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: isBot ? 'row' : 'row-reverse', alignItems: 'flex-start', maxWidth: '70%' }}>
          <Avatar sx={{ bgcolor: isBot ? 'primary.main' : 'secondary.main', mr: isBot ? 1 : 0, ml: isBot ? 0 : 1 }}>
            {isBot ? <SupportAgentIcon /> : <UserAvatar />}
          </Avatar>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="body2" component="div">
                {message.content.split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
                {message.mediaType === 'image' && (
                  <CardMedia
                    component="img"
                    image={message.mediaUrl}
                    alt="Image response"
                    sx={{ borderRadius: 1, mb: 1, maxHeight: 200, objectFit: 'contain' }}
                  />
                )}
                {message.mediaType === 'video' && (
                  <CardMedia
                    component="video"
                    src={message.mediaUrl}
                    controls
                    sx={{ borderRadius: 1, mb: 1, maxHeight: 200 }}
                  />
                )}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ height: '75vh', display: 'flex', flexDirection: 'column', bgcolor: 'grey.100' }}>
      <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Enter API endpoint (e.g., http://ip:port)"
          value={endpoint}
          onChange={(e) => setEndpoint(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <HttpIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>
      <Paper elevation={3} sx={{ flex: 1, overflow: 'auto', p: 1, bgcolor: 'background.default' }}>
        {messages.map(renderMessage)}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        <div ref={messagesEndRef} />
      </Paper>
      <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
        <TextField
          disabled={isLoading}
          fullWidth
          variant="outlined"
          placeholder="Type your message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSend()}
          InputProps={{
            endAdornment: (
              <Stack direction="row" spacing={2}>
                <IconButton color="error" onClick={handleDelete} disabled={isLoading}>
                  <DeleteIcon />
                </IconButton>
                <IconButton color="primary" onClick={handleSend} disabled={isLoading}>
                  {isLoading ? <CircularProgress size={24} /> : <SendIcon />}
                </IconButton>
              </Stack>
            ),
          }}
        />
      </Box>
    </Box>
  );
};

export default ChatBot;